import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useAuthStore } from '@/common/zustand/store/auth.store'
import { hasActiveSession } from '@/auth/auth.utils'
import { decideAuthFlow } from './authFlow'
import { useAuthUrlParams } from './useAuthUrlParams'
import RedirectFallback from './RedirectFallback'
import { matchPath } from 'react-router-dom'

export default function ProteccionDeRutas({ children, rutasConfig }) {
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const hasStoreToken = useAuthStore((s) => Boolean(s.token))
  const cleanup = useAuthStore((s) => s.cleanup)
  const storeIdCertificado = useAuthStore((s) => s.idCertificado)

  const { pathname, normalizedPathname, urlUuid, idcertificadoUrl } = useAuthUrlParams()

  const [loading, setLoading] = useState(true)
  const [auth, setAuth] = useState(false)

  const rutaActual = rutasConfig?.find((r) => matchPath({ path: r.path, end: true }, normalizedPathname))
  const requiereIdCertificado = rutaActual?.meta?.requiereIdCertificado ?? true

  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const flow = decideAuthFlow({
        requiereIdCertificado,
        urlUuid,
        hasStoreToken,
        hasCookie: hasActiveSession(),
        idcertificadoUrl,
        storeIdCertificado,
      })

      if (flow.action === 'authenticated' || flow.action === 'no-session') {
        if (!cancelled) {
          setAuth(flow.action === 'authenticated')
          setLoading(false)
        }
        return
      }

      try {
        const ok = await checkAuth(flow.params)
        if (!cancelled) {
          setAuth(flow.hasUuid ? Boolean(ok || useAuthStore.getState().token) : ok)
        }
      } catch {
        if (!cancelled) setAuth(false)
      } finally {
        if (flow.hasUuid && !cancelled) {
          const { pathname: current } = window.location
          window.history.replaceState({}, '', current)
        }
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [checkAuth, hasStoreToken, idcertificadoUrl, requiereIdCertificado, storeIdCertificado, urlUuid])

  return (
    <RedirectFallback
      loading={loading}
      auth={auth}
      pathname={pathname}
      normalizedPathname={normalizedPathname}
      requiereIdCertificado={requiereIdCertificado}
      idcertificadoUrl={idcertificadoUrl}
      storeIdCertificado={storeIdCertificado}
    >
      {children}
    </RedirectFallback>
  )
}

ProteccionDeRutas.propTypes = {
  children: PropTypes.node.isRequired,
  rutasConfig: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      meta: PropTypes.shape({
        requiereIdCertificado: PropTypes.bool,
      }),
    })
  ),
}
